// The segmented control: one exclusive axis in a sunken track (#11426
// gaps-v10, CD rulings 1/3/c). The track is the well (--fp-bg-well) and the
// ACTIVE segment is white — it rises out of the track the way a card rises
// off the page, which is the tonal step the product uses everywhere else.
// This is THE segmented recipe; other segmented tracks migrate to it in
// their own sweep.

import React from "react";
import { moveTabFocus } from "./rovingTabs";

export interface SegmentedOption<K extends string> {
  key: K;
  label: string;
  /**
   * Counts render in tabular nums beside the label. Omit for none.
   * `"pending"` holds the count's place while it is being read; with the
   * track's `reserveCounts` the number then lands in the same box.
   */
  count?: number | "pending";
  /** A glyph before the label (Appearance's Record pages | Widgets, #13370 round 33). */
  icon?: React.ReactNode;
  /** A quiet word after the label, after a middle dot ("Files · Hidden", #13370 round 36g). */
  note?: string;
  /**
   * A hairline before this segment: the axis changes meaning here (the
   * lifecycle states, then a KIND of org — Claude Design, admin
   * Organizations consult, 2026-09-27). A divider means a change of object,
   * never a visual breather, the Menu's rule.
   */
  dividerBefore?: boolean;
}

export interface SegmentedProps<K extends string> {
  options: ReadonlyArray<SegmentedOption<K>>;
  value: K;
  onChange: (key: K) => void;
  "aria-label": string;
  /**
   * For a track whose counts arrive after it renders: every count slot takes
   * the same box, before and after its number lands, so the segments never
   * change size.
   */
  reserveCounts?: boolean;
  /**
   * "compact" draws a 30px control (a 2px track pad, tighter segments) for a row of 30px tools
   * (Appearance's Table | List toggle, #13370 round 37). The default is the page's full size.
   */
  size?: "default" | "compact";
}

/**
 * The box a reserved count slot takes: three tabular digits, so a placeholder
 * and the number that replaces it (up to 999) occupy the same width.
 */
const RESERVED_COUNT = "inline-block min-w-[3ch]";

export const Segmented = <K extends string>({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
  reserveCounts = false,
  size = "default",
}: SegmentedProps<K>) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    // One tab stop, on the chosen segment; the arrow keys move between segments (#11849 Round B).
    onKeyDown={moveTabFocus}
    // Track: the well, 11px radius, 4px pad → segments at the 7px control
    // radius (11 − 4), per the CD ruling. Rounder and roomier than a button
    // row, deliberately — a well with tight corners reads as a row of
    // buttons that happen to share a box.
    className={`inline-flex items-center gap-0.5 bg-[var(--fp-bg-well)] ${
      size === "compact" ? "rounded-[9px] p-[2px]" : "rounded-[11px] p-[4px]"
    }`}
  >
    {options.map((option) => {
      const selected = option.key === value;
      return (
        <React.Fragment key={option.key}>
          {option.dividerBefore && (
            <span
              aria-hidden="true"
              className="mx-[3px] h-4 w-px self-center bg-[var(--fp-border-strong)]"
            />
          )}
          <button
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            // A click on the already-active segment is a strict no-op
            // (standing CD rule, gaps-v9).
            onClick={selected ? undefined : () => onChange(option.key)}
            // No border on the active chip: it is white sitting in a well 14
            // points darker, so the well's own edge already draws it.
            // Inactive labels are ink-2, not ink-3 — the ramp is measured on
            // the page grounds and ink-3 falls to 4.04:1 on the well (CD
            // cross-cutting ruling B).
            className={`flex items-center gap-[6px] whitespace-nowrap rounded-[7px] text-[12.5px] font-medium transition-colors ${
              size === "compact" ? "px-[10px] py-[4px]" : "px-[15px] py-[7px]"
            } ${
              selected
                ? "bg-[var(--fp-surface)] text-[var(--fp-ink)]"
                : "text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]"
            }`}
          >
            {option.icon}
            {option.label}
            {option.note !== undefined && (
              <span className="text-[var(--fp-ink-3)]">{` · ${option.note}`}</span>
            )}
            {option.count === "pending" ? (
              // The count's place while it is read: a quiet bar, hidden from
              // assistive tech, so the tab is named by its label alone.
              <span
                aria-hidden
                data-count-pending
                className={`${reserveCounts ? RESERVED_COUNT : ""} text-[11.5px]`}
              >
                <span className="inline-block h-[0.7em] w-[2ch] rounded-[3px] bg-[var(--fp-border)] align-[-0.05em]" />
              </span>
            ) : option.count !== undefined ? (
              <span
                className={`text-[11.5px] [font-variant-numeric:tabular-nums] ${
                  reserveCounts ? RESERVED_COUNT : ""
                } ${
                  selected ? "text-[var(--fp-ink-3)]" : "text-[var(--fp-ink-2)]"
                }`}
              >
                {option.count}
              </span>
            ) : null}
          </button>
        </React.Fragment>
      );
    })}
  </div>
);
